/**
 * useExecuteNodeAction
 *
 * Wraps `client.ts:executeNode` for canvas Generate buttons. Provides:
 *   1. A 1s cooldown per nodeId (via useClickLock) so a fast double-tap
 *      can't fire /execute-node twice. Holds the lock while the request
 *      is in flight PLUS 1s of post-call cooldown so the YDoc-driven
 *      `idle → running` status flip has time to disable the button via
 *      the existing `isRunning` guard.
 *   2. InsufficientSparksError catch — opens the workspace-level
 *      <InsufficientSparksModal> via InsufficientSparksContext instead
 *      of letting the error bubble to console.error.
 *   3. `cancelExecution()` — cancel a running node via the backend.
 *
 * Returns:
 *   - `isLocked` — true while the cooldown window is active for this
 *     node. Buttons should `disabled={isRunning || isLocked}`.
 *   - `runExecute(retryCount)` — fire /execute-node with cooldown
 *     + insufficient-sparks handling.
 *   - `cancelExecution()` — cancel a running node execution.
 *   - `isCancelling` — true while the cancel request is in flight.
 */

import { useCallback, useState } from 'react'
import { executeNode, cancelNodeExecution, InsufficientSparksError } from '@ponpon/api-client'
import { useShowInsufficientSparks } from '../contexts/InsufficientSparksContext'
import { useClickLock } from './useClickLock'

interface UseExecuteNodeActionOptions {
  projectId: string | undefined
  nodeId: string
  /** Status from YDoc — block firing while already running/queued. */
  isRunning: boolean
  cooldownMs?: number
  /** Optional action override (e.g. 'regenerate_voice'). Omit for default 'generate'. */
  action?: string
}

export interface UseExecuteNodeActionResult {
  isLocked: boolean
  runExecute: (retryCount?: number) => void
  cancelExecution: () => void
  isCancelling: boolean
}

export function useExecuteNodeAction({
  projectId,
  nodeId,
  isRunning,
  cooldownMs = 1000,
  action,
}: UseExecuteNodeActionOptions): UseExecuteNodeActionResult {
  const { isLocked: isLockedKey, run } = useClickLock(cooldownMs)
  const showInsufficientSparks = useShowInsufficientSparks()
  const [isCancelling, setIsCancelling] = useState(false)

  const runExecute = useCallback(
    (retryCount: number = 1) => {
      if (isRunning || !projectId) return
      void run(nodeId, async () => {
        try {
          await executeNode(projectId, nodeId, action, retryCount)
        } catch (err) {
          if (err instanceof InsufficientSparksError) {
            // Capture the same arguments in a retry closure so the
            // UsageCostModal can auto-resume the action once the user
            // tops up enough credits — saves them hunting for the node.
            const retry = () => executeNode(projectId, nodeId, action, retryCount)
            showInsufficientSparks({
              balance: err.balance,
              required: err.required,
              nodeId,
              retry,
            })
            return
          }
          // eslint-disable-next-line no-console
          console.error('executeNode failed:', err)
        }
      })
    },
    [isRunning, projectId, nodeId, action, run, showInsufficientSparks],
  )

  const cancelExecution = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    cancelNodeExecution(projectId, nodeId)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => {
        setIsCancelling(false)
      })
  }, [isRunning, projectId, nodeId, isCancelling])

  return {
    isLocked: isLockedKey(nodeId),
    runExecute,
    cancelExecution,
    isCancelling,
  }
}
